import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { debounce, isEmpty } from "lodash";
import { useTask, useTaskDefs } from "../data/task";
import { NEW_TASK_TEMPLATE } from "../schema/task";
import { useTaskNames } from "../hooks/useTaskNames";
import { isValidWorkflowName } from "../utils/workflowDefinition/workflowDefinitionUtils";
/**
 * Custom hook for managing task definition logic
 * @param {string} initialTaskName - The initial task name (for edit mode)
 * @param {string} mode - The mode: 'create' or 'edit'
 * @returns {object} Task definition state and handlers
 */
export function useTaskDefinition(initialTaskName = null) {

    // State management
    const [data, setData] = useState({});
    const [isModified, setIsModified] = useState(false);
    const [jsonErrors, setJsonErrors] = useState([]);
    const [resetDialog, setResetDialog] = useState(false);
    const [saveDialog, setSaveDialog] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);

    // Refs
    const editorRef = useRef();
    const debouncedHandleChange = useRef(null);

    // API calls
    const {
        data: taskDef,
        isFetching,
        refetch,
    } = useTask(initialTaskName, NEW_TASK_TEMPLATE);

    const { taskNames,
        refetchTasks, isFetchingTasks } = useTaskNames()


    const taskJson = useMemo(() => {
        let sorted_obj = data
            ? Object.keys(data)
                .sort()
                .reduce((temp_obj, key) => {
                    temp_obj[key] = data[key];
                    return temp_obj;
                }, {})
            : {};
        return data ? JSON.stringify(sorted_obj, null, 2) : "";
    }, [data]);

    const { data: originalData } = useTask(initialTaskName, NEW_TASK_TEMPLATE);

    const originalTaskJson = useMemo(() => {
        let sorted_obj = originalData
            ? Object.keys(originalData)
                .sort()
                .reduce((temp_obj, key) => {
                    temp_obj[key] = originalData[key];
                    return temp_obj;
                }, {})
            : "";
        return originalData ? JSON.stringify(sorted_obj, null, 2) : "";
    }, [originalData]);

    // Handlers
    const handleTasks = useCallback((val) => {
        if (!isEmpty(data) && JSON.stringify(val) !== JSON.stringify(data)) {
            setData(val);
            setIsModified(JSON.stringify(originalData) !== JSON.stringify(data));
        }
    }, [data, originalData]);

    const handleOpenSave = useCallback(() => {
        setSaveDialog({
            original: initialTaskName ? originalTaskJson : "",
            originalObj: initialTaskName ? originalData : null,
            modified: taskJson || null,
        });
    }, [initialTaskName, originalTaskJson, originalData, taskJson]);

    const handleSaveSuccess = useCallback((name, navigate, loadedFrom, workflowName) => {
        setSaveDialog(null);
        setIsModified(false);
        if (loadedFrom === "workflowDefinition") {
            if (isValidWorkflowName(workflowName)) {

                navigate(`/workflowDef/${workflowName}`)
            }
            else {
                navigate(`/workflowDef`)
            }
        }
        if (name === initialTaskName) {
            refetch();
        } else if (navigate) {
            navigate(`/taskDef/${name}`);
        }
    }, [initialTaskName, refetch]);

    const doReset = useCallback(() => {
        if (!editorRef?.current?.getModel) return;
        refetch();
        setData(taskDef);
        setResetDialog(false);
        setIsModified(false);
    }, [refetch, taskDef]);

    const handleValidate = useCallback((markers) => {
        setJsonErrors(markers);
    }, []);

    const handleChange = useCallback((v) => {
        try {
            const parsedNewWorkflow = JSON.parse(v);
            if (parsedNewWorkflow) {
                setData(parsedNewWorkflow);
                setIsModified(v !== taskJson);
            }
        } catch (e) {
            console.error(e);
        }
    }, [taskJson]);

    const handleEditorChange = useCallback((value, event) => {
        const position = editorRef.current?.getPosition();
        if (value === "") {
            handleChange(value);
            return;
        }

        if (debouncedHandleChange.current) {
            debouncedHandleChange.current.cancel();
        }

        debouncedHandleChange.current = debounce((value) => {
            handleChange(value);
            setTimeout(() => {
                editorRef.current?.setPosition(position);
            }, 0);
        }, 1000);

        debouncedHandleChange.current(value);
    }, [handleChange]);

    const handleTabChange = useCallback((event, newValue) => {
        setTabIndex(newValue);
        if (newValue !== 1 && editorRef?.current?.getModel()) {
            if (debouncedHandleChange.current) {
                debouncedHandleChange.current.cancel();
            }
        }
    }, []);

    const checkExistingTask = useCallback(() => {
        const taskdefname = data.name;
        return taskNames?.includes(taskdefname);
    }, [data.name, taskNames]);

    // Monaco editor handlers
    const handleEditorWillMount = useCallback((monaco) => {
        // Import configureMonaco here to avoid circular dependencies
        const { configureMonaco } = require("../schema/task");
        configureMonaco(monaco);
    }, []);

    const handleEditorDidMount = useCallback((editor) => {
        editorRef.current = editor;
    }, []);

    // Effects
    useEffect(() => {
        if (taskDef) {

            setData(taskDef);
        }
    }, [taskDef]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (debouncedHandleChange.current) {
                debouncedHandleChange.current.cancel();
            }
        };
    }, []);

    return {
        // State
        data,
        setData,
        isModified,
        setIsModified,
        jsonErrors,
        setJsonErrors,
        resetDialog,
        setResetDialog,
        saveDialog,
        setSaveDialog,
        tabIndex,
        setTabIndex,
        isFetching,

        // Computed values
        taskNames,
        taskJson,
        originalData,
        originalTaskJson,
        taskDef,

        // Handlers
        handleTasks,
        handleOpenSave,
        handleSaveSuccess,
        doReset,
        handleValidate,
        handleChange,
        handleEditorChange,
        handleTabChange,
        checkExistingTask,
        handleEditorWillMount,
        handleEditorDidMount,

        // Refs
        editorRef,

        // API methods
        refetch,
        refetchTasks,
    };
}